import React, { forwardRef, ReactElement } from 'react';
import { Dialog } from 'react-aria-components/Dialog';
import { I18nProvider } from 'react-aria-components/I18nProvider';
import {
  CalendarCell,
  CalendarGrid,
  DateSegment,
  Popover,
} from 'react-aria-components/DatePicker';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { LABEL_BEFORE, LABEL_IN_CORNER } from '../../../styles/fontsStyleUtils';
import { FORM_FIELD_ERROR_ARIA_LIVE_VALUE } from './Forms';
import {
  CommonInputHelperText,
  CommonInputHelperWrapper,
  CommonInputInner,
  CommonInputLabelWrapper,
  CommonInputWrapper,
} from './Forms.styled';
import { CommonInput, GlobalInputProps } from './Input';
import { RequiredLabel } from './RequiredLabel';
import {
  DatePicker,
  DatePickerCalendar,
  DatePickerHeaderButton,
  DatePickerHeaderDate,
  DatePickerHeaderWrapper,
  DatePickerIcon,
  DatePickerDateInput,
  DatePickerLabel,
  DatePickerGroup,
} from './DatePickerInput.styled';
import { isMobile } from 'react-device-detect';
import {
  DatePickerInputOnChange,
  DatePickerInputValue,
  useDatePickerInputData,
} from './DatePickerInput.hooks';

export const DATE_PICKER_CSS_CLASS_NAME = 'sgx-date-picker-input';

export type GlobalDatePickerProps = Omit<
  GlobalInputProps,
  'onChange' | 'icon' | 'iconRight'
> & {
  value: DatePickerInputValue;
  onChange: DatePickerInputOnChange;
  maxDate?: Date;
  minDate?: Date;
  sizing?: 'MIN_CONTENT' | 'UNDEFINED';
  label?: React.ReactNode;
  required?: boolean;
  width?: string;
  flexWidth?: boolean;
  hideDateFormatInLabel?: boolean;
  containerStyle?: React.CSSProperties;
  popperZIndex?: number;
  filterDate?: (date: Date) => boolean | undefined;
};

export const DatePickerInput = forwardRef<
  HTMLInputElement,
  GlobalDatePickerProps
>(
  (
    {
      id,
      name,
      onChange,
      value,
      placeholder,
      disabled,
      readOnly,
      maxDate,
      minDate,
      sizing,
      label,
      hideDateFormatInLabel,
      containerStyle,
      required,
      popperZIndex,
      filterDate,
      className,
      flexWidth,
      width,
      fontBasedSize,
      labelPosition = LABEL_IN_CORNER,
      noSpaceForHelperText,
      helperText,
      hasError,
      fixedLabelWidth,
      ...rest
    },
    ref,
  ): ReactElement => {
    const inputId = id || name;
    const helperTextId = `${inputId}-helper-text`;

    const {
      handleOnChange,
      handleCopy,
      handlePaste,
      inputValue,
      defaultValue,
      renderKey,
      isDateUnavailable,
      maxValue,
      minValue,
      placeholderValue,
    } = useDatePickerInputData({
      usingCustomInput: isMobile,
      onChange,
      value,
      maxDate,
      minDate,
      filterDate,
    });

    /**
     * When in mobile style date picker is rendered via "date" type,
     * which means it is up to the platform such as android to choose
     * format. So in that case we don't want to show the user the format.
     */
    const dateFormatForUser = isMobile ? undefined : DATE_FORMAT_FOR_USER;
    let dateLabel = label;
    if (!hideDateFormatInLabel && dateFormatForUser) {
      if (dateLabel) {
        dateLabel = `${dateLabel} (${dateFormatForUser})`;
      } else {
        dateLabel = `(${dateFormatForUser})`;
      }
    }

    className = [DATE_PICKER_CSS_CLASS_NAME, className]
      .filter(Boolean)
      .join(' ');

    return isMobile ? (
      <CommonInput
        {...rest}
        onChange={(event) => handleOnChange(event.target.value)}
        value={inputValue}
        label={<RequiredLabel label={dateLabel} required={required} />}
        name={name}
        ref={ref}
        type={'date'}
        className={className}
        noSpaceForHelperText={noSpaceForHelperText}
        disabled={disabled}
        id={inputId}
        hasError={hasError}
        helperText={helperText}
        fixedLabelWidth={fixedLabelWidth}
        width={width}
        flexWidth={flexWidth}
        containerStyle={containerStyle}
        fontBasedSize={fontBasedSize}
      />
    ) : (
      <CommonInputWrapper
        key={renderKey}
        className={className}
        flexWidth={flexWidth}
        width={width}
        style={containerStyle}
        fontBasedSize={fontBasedSize}
      >
        <I18nProvider locale={LOCALE}>
          <DatePicker
            isDisabled={disabled}
            isReadOnly={readOnly}
            maxValue={maxValue}
            minValue={minValue}
            isDateUnavailable={isDateUnavailable}
            defaultValue={defaultValue}
            onChange={handleOnChange}
            ref={ref}
            name={name}
            id={inputId}
            placeholderValue={placeholderValue}
          >
            <CommonInputLabelWrapper
              labelPosition={labelPosition}
              labelComponent={DatePickerLabel}
              disabled={disabled}
              fontBasedSize={fontBasedSize}
            >
              <CommonInputHelperWrapper
                noSpaceForHelperText={noSpaceForHelperText}
              >
                <CommonInputInner isDisabled={disabled}>
                  <DatePickerGroup
                    onCopy={handleCopy}
                    onPaste={readOnly || disabled ? undefined : handlePaste}
                  >
                    <DatePickerDateInput
                      onKeyDown={clickFormDefaultButtonOnEnter}
                      $styledProps={{
                        ...rest,
                        hasError,
                        disabled,
                        readOnly,
                        hasIcon: true,
                        iconPosition: 'right',
                        labelComponent: DatePickerLabel,
                      }}
                    >
                      {({ text, ...segment }) => (
                        <DateSegment
                          segment={{
                            ...segment,
                            text:
                              text === DATE_SEGMENT_ORIGINAL_DELIMITER
                                ? DATE_SEGMENT_DISPLAYED_DELIMITER
                                : text,
                          }}
                        />
                      )}
                    </DatePickerDateInput>
                    {dateLabel && labelPosition === LABEL_IN_CORNER && (
                      <DatePickerLabel $styledProps={{}}>
                        <RequiredLabel label={dateLabel} required={required} />
                      </DatePickerLabel>
                    )}
                    <DatePickerIcon disabled={disabled} iconPosition="right">
                      <FaCalendarAlt />
                    </DatePickerIcon>
                  </DatePickerGroup>
                  <Popover placement="bottom left">
                    <Dialog>
                      <DatePickerCalendar>
                        <DatePickerHeaderWrapper>
                          <DatePickerHeaderButton
                            slot="previous"
                            size="auto"
                            variant="primary"
                            iconOnly
                            type="button"
                          >
                            <FaChevronLeft />
                          </DatePickerHeaderButton>
                          <DatePickerHeaderDate />
                          <DatePickerHeaderButton
                            slot="next"
                            size="auto"
                            variant="primary"
                            iconOnly
                            type="button"
                          >
                            <FaChevronRight />
                          </DatePickerHeaderButton>
                        </DatePickerHeaderWrapper>
                        <CalendarGrid>
                          {(date) => <CalendarCell date={date} />}
                        </CalendarGrid>
                      </DatePickerCalendar>
                    </Dialog>
                  </Popover>
                </CommonInputInner>
                {Boolean(helperText) && !disabled && (
                  <CommonInputHelperText
                    hasError={hasError}
                    id={helperTextId}
                    aria-live={
                      hasError ? FORM_FIELD_ERROR_ARIA_LIVE_VALUE : undefined
                    }
                  >
                    {helperText}
                  </CommonInputHelperText>
                )}
              </CommonInputHelperWrapper>
              {dateLabel && labelPosition === LABEL_BEFORE && (
                <DatePickerLabel
                  elementType="span"
                  $styledProps={{ fixedLabelWidth }}
                >
                  <RequiredLabel label={dateLabel} required={required} />
                </DatePickerLabel>
              )}
            </CommonInputLabelWrapper>
          </DatePicker>
        </I18nProvider>
      </CommonInputWrapper>
    );
  },
);

/**
 * Date segments are contentEditable spans, so the browser does not submit
 * the form when the user presses Enter in them. Do what the browser does
 * for an <input>: click the first submit button of the form.
 */
const clickFormDefaultButtonOnEnter = (
  event: React.KeyboardEvent<HTMLElement>,
) => {
  if (event.key !== 'Enter') {
    return;
  }
  const formElements = event.currentTarget.closest('form')?.elements ?? [];
  const defaultButton = Array.from(formElements).find(
    (element): element is HTMLButtonElement | HTMLInputElement =>
      (element instanceof HTMLButtonElement ||
        element instanceof HTMLInputElement) &&
      element.type === 'submit',
  );
  defaultButton?.click();
};

const LOCALE = 'en-GB';
const DATE_SEGMENT_ORIGINAL_DELIMITER = '/';
const DATE_SEGMENT_DISPLAYED_DELIMITER = '.';
const DATE_FORMAT_FOR_USER = `dd${DATE_SEGMENT_DISPLAYED_DELIMITER}mm${DATE_SEGMENT_DISPLAYED_DELIMITER}yyyy`;
